/**
 * Identifier conventions (WP1-12) - query + mutation layer.
 *
 * `GET /api/identifiers/config/` returns both schemes (holding + claim) with
 * their code rows and live `next_value`s, plus the tenant's categories and
 * product templates so the settings page can build the code grid from real
 * data. `PUT /api/identifiers/schemes/<resource_type>/` upserts one scheme.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'

export type IdentifierResourceType = 'holding' | 'claim'

export type IdentifierPrefixSource = 'static' | 'category' | 'product_template'

export type IdentifierYearMode = 'yyyy' | 'yy' | 'none'

/** The closed vocabulary of format segments; the admin arranges these. */
export type IdentifierSegment = 'prefix' | 'year' | 'code' | 'seq'

/** The two independently-resolved code slots. */
export type IdentifierSlot = 'prefix' | 'code'

export type IdentifierCodeSourceKind =
  'static' | 'fallback' | 'category' | 'product_template'

export interface IdentifierCode {
  slot: IdentifierSlot
  source_kind: IdentifierCodeSourceKind
  /** Natural key (category slug / template slug); '' for static & fallback. */
  ref_key: string
  code: string
  next_value: number
}

export interface IdentifierScheme {
  is_enabled: boolean
  /** Ordered list of segments; drives which appear and in what order. */
  segment_order: IdentifierSegment[]
  prefix_source: IdentifierPrefixSource
  /** Source for the optional `code` slot; '' when there is no code segment. */
  code_source: IdentifierPrefixSource | ''
  year_mode: IdentifierYearMode
  padding: number
  start_at: number | null
  codes: IdentifierCode[]
}

export interface IdentifierSourceOption {
  ref_key: string
  label: string
  product_count: number
}

export interface IdentifierConfig {
  schemes: {
    holding: IdentifierScheme | null
    claim: IdentifierScheme | null
  }
  sources: {
    categories: IdentifierSourceOption[]
    product_templates: IdentifierSourceOption[]
  }
  /** Present only when the tenant has the vouchers module. */
  vouchers?: VoucherIdentifiers
}

export type VoucherIdentifierType = 'voucher' | 'voucher_confirm'

/** A voucher scheme: segments may include `parent` (the claim) and `random`. */
export interface VoucherIdentifierScheme extends Omit<
  IdentifierScheme,
  'segment_order'
> {
  segment_order: string[]
}

export interface VoucherIdentifiers {
  /** The schemes in force: the tenant's saved one, else the built-in one. */
  schemes: Record<VoucherIdentifierType, VoucherIdentifierScheme>
  /** A real claim reference from this tenant, for the preview. */
  sample_parent: string
}

/** PUT body: the scheme minus per-code `next_value` (counters are read-only). */
export type IdentifierCodeInput = Omit<IdentifierCode, 'next_value'>

export interface IdentifierSchemePayload extends Omit<
  IdentifierScheme,
  'codes'
> {
  codes: IdentifierCodeInput[]
}

export function useIdentifierConfig() {
  return useQuery<IdentifierConfig>({
    queryKey: ['identifier-config'],
    queryFn: () => api.get('/identifiers/config/').then((r) => r.data),
  })
}

export function useUpdateIdentifierScheme() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      resourceType,
      scheme,
    }: {
      resourceType: IdentifierResourceType
      scheme: IdentifierSchemePayload
    }) =>
      api
        .put(`/identifiers/schemes/${resourceType}/`, scheme)
        .then((r) => r.data as IdentifierScheme),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['identifier-config'] })
    },
  })
}

export function useUpdateVoucherIdentifierScheme() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      resourceType,
      scheme,
    }: {
      resourceType: VoucherIdentifierType
      scheme: Omit<VoucherIdentifierScheme, 'codes'> & {
        codes: IdentifierCodeInput[]
      }
    }) =>
      api
        .put(`/identifiers/schemes/${resourceType}/`, scheme)
        .then((r) => r.data as VoucherIdentifierScheme),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['identifier-config'] })
    },
  })
}
